import React, { useState } from 'react';
import { Wrench, Sparkles, Shield, Globe, BookOpen } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useTranslation } from '../LanguageContext';
import { Language } from '../types';

interface LoginScreenProps {
  onShowLicense: () => void;
  onShowManual: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onShowLicense, onShowManual }) => {
  const { language, setLanguage } = useTranslation();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const texts: Record<string, Record<Language, string>> = {
    rev: { EN: 'Revolutionizing', KH: 'បដិវត្តន៍', ZH: '革新', JA: '革命を起こす', KO: '혁신적인' },
    market: { EN: 'Repair Markets', KH: 'ទីផ្សារជួសជុល', ZH: '维修市场', JA: '修理市場', KO: '수리 시장' },
    desc: { 
      EN: 'Connect with trusted technicians and manage your repairs with AI diagnostics.',
      KH: 'ភ្ជាប់ទំនាក់ទំនងជាមួយជាងដែលមានជំនាញច្បាស់លាស់ និងគ្រប់គ្រងការជួសជុលរបស់អ្នកជាមួយបច្ចេកវិទ្យា AI។',
      ZH: '与值得信赖的技师建立联系，并通过 AI 诊断管理您的维修。',
      JA: '信頼できる技術者とつながり、AI 診断で修理を管理します。',
      KO: '신뢰할 수 있는 기술자와 연결하고 AI 진단으로 수리를 관리하세요.'
    },
    google: { EN: 'Continue with Google', KH: 'បន្តជាមួយ Google', ZH: '使用 Google 继续', JA: 'Google で続行', KO: 'Google로 계속하기' },
    testing_notice: { 
      EN: 'It is for TESTING. Data will be reset next month',
      KH: 'នេះសម្រាប់តែការធ្វើតេស្តប៉ុណ្ណោះ។ ទិន្នន័យនឹងត្រូវលុបចោលនៅខែក្រោយ',
      ZH: '仅用于测试。数据将在下个月重置',
      JA: 'これはテスト用です。データは来月リセットされます',
      KO: '테스트용입니다. 데이터는 다음 달에 초기화됩니다'
    },
    loading: { EN: 'Initializing...', KH: 'កំពុងដំណើរការ...', ZH: '初始化中...', JA: '初期化中...', KO: '초기화 중...' },
    error: { EN: 'Authentication failed', KH: 'ការចូលប្រព័ន្ធបានបរាជ័យ', ZH: '身份验证失败', JA: '認証に失敗しました', KO: '인증 실패' },
    manual: { EN: 'Manual', KH: 'សៀវភៅណែនាំ', ZH: '手册', JA: 'マニュアル', KO: '매뉴얼' }
  };

  const langs: { code: Language; label: string }[] = [
    { code: 'KH', label: 'ខ្មែរ' },
    { code: 'EN', label: 'EN' },
    { code: 'ZH', label: '中文' },
    { code: 'JA', label: '日本語' },
    { code: 'KO', label: '한국어' },
  ];

  const handleGoogleLogin = async () => {
    setIsAuthenticating(true);
    setErrorMsg(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;
      
    } catch (error: any) {
      console.error("Login failed:", error);
      setErrorMsg(error.message || texts.error[language]);
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-slate-900">
      {/* Dynamic Background */}
      <div className="absolute top-0 left-0 w-full h-full">
         <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-violet-600/30 blur-[120px]"></div>
         <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-fuchsia-600/30 blur-[120px]"></div>
      </div>

      {/* Language Switcher Overlay - Positioned Top Middle */}
      <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-700 w-full max-w-fit px-4">
        <div className="bg-white/10 backdrop-blur-md p-1.5 rounded-2xl border border-white/10 flex items-center gap-1 shadow-2xl overflow-x-auto no-scrollbar">
          <div className="px-3 py-1 flex items-center gap-2 text-white/40 shrink-0">
            <Globe size={14} />
          </div>
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => setLanguage(l.code)}
              className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 shrink-0 ${
                language === l.code 
                  ? 'bg-white text-slate-900 shadow-xl scale-105' 
                  : 'text-white/40 hover:text-white hover:bg-white/5'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full max-w-4xl bg-white/5 backdrop-blur-2xl rounded-[3rem] border border-white/10 shadow-2xl p-8 lg:p-16 relative z-10 flex flex-col items-center text-center">
        
        <div className="inline-flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-tr from-violet-600 to-fuchsia-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/50">
              <Wrench className="text-white" size={24} />
          </div>
          <span className="text-3xl font-black text-white tracking-widest uppercase">Cheang</span>
        </div>
        
        <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6 max-w-2xl">
          {texts.rev[language]} <br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">{texts.market[language]}</span>
        </h1>
        
        <p className="text-slate-300 text-lg leading-relaxed max-w-lg mb-12">
          {texts.desc[language]}
        </p>

        <div className="w-full max-w-sm space-y-4">
          <button 
            onClick={handleGoogleLogin}
            disabled={isAuthenticating}
            className="w-full py-5 px-6 bg-white text-slate-900 rounded-[2rem] font-black text-lg hover:bg-slate-100 transition-all flex flex-col items-center justify-center shadow-2xl shadow-white/5 group"
          >
            {isAuthenticating ? (
              <span className="flex items-center">
                <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin mr-3"></div>
                {texts.loading[language]}
              </span>
            ) : (
              <>
                <span className="flex items-center mb-1">
                  <svg className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {texts.google[language]}
                </span>
                <span className="text-[10px] font-black uppercase text-rose-500 tracking-tight leading-tight">
                  {texts.testing_notice[language]}
                </span>
              </>
            )}
          </button>
          
          <div className="flex flex-col items-center gap-6 pt-8">
             <button 
               onClick={onShowLicense}
               className="group flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all"
             >
               <Shield size={14} className="text-violet-400" />
               <span className="text-[10px] font-black text-white/40 group-hover:text-white uppercase tracking-widest transition-colors">
                 អាជ្ញាប័ណ្ណសហគមន៍ / Community License
               </span>
             </button>

             <div className="flex items-center gap-4">
                <button 
                  onClick={onShowManual}
                  className="flex items-center gap-1.5 text-[9px] font-black text-slate-500 hover:text-white transition-colors uppercase tracking-[0.2em]"
                >
                  <BookOpen size={12} className="text-violet-400/60" /> {texts.manual[language]}
                </button>
                <div className="w-px h-3 bg-white/10"></div>
                <div className="text-[9px] font-black text-slate-600 uppercase tracking-[0.2em]">
                  Ver 0.01 (Preview)
                </div>
             </div>
          </div>

          {errorMsg && (
            <p className="text-rose-400 text-sm font-bold bg-rose-900/20 py-3 px-6 rounded-2xl animate-in shake">{errorMsg}</p>
          )}
        </div>
      </div>
    </div>
  );
};